import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Radio, Clock, Loader2, Globe2, Calendar } from "lucide-react";
import api from "@/lib/api";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// Build the next occurrence (as a real Date in UTC) of a WAT-scheduled programme.
// WAT = UTC+1, no DST.  For a programme at H:M WAT:
//   UTC hour = (H - 1 + 24) % 24, adjusting the date if H == 0.
// day_of_week is -1 (every day) or 0..6 with Monday = 0.
const nextOccurrence = (prog, from = new Date()) => {
  const nowUtc = new Date(from.toISOString());
  // Compute UTC hour/minute + day offset
  let utcHour = prog.hour_wat - 1;
  let dayOffset = 0;
  if (utcHour < 0) { utcHour += 24; dayOffset -= 1; }

  const nowMs = nowUtc.getTime();
  // Search up to 8 days ahead
  for (let add = 0; add < 8; add++) {
    const candidate = new Date(Date.UTC(
      nowUtc.getUTCFullYear(),
      nowUtc.getUTCMonth(),
      nowUtc.getUTCDate() + add + dayOffset,
      utcHour,
      prog.minute_wat || 0,
      0,
    ));
    // JS getUTCDay: 0=Sun..6=Sat → convert to Mon=0..Sun=6
    const jsDow = candidate.getUTCDay();
    const monDow = jsDow === 0 ? 6 : jsDow - 1;
    if (prog.day_of_week !== -1 && prog.day_of_week !== monDow) continue;
    if (candidate.getTime() > nowMs) return candidate;
  }
  return null;
};

const formatWATTime = (prog) => {
  const h = prog.hour_wat;
  const m = String(prog.minute_wat || 0).padStart(2, "0");
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${m} ${ampm}`;
};

const formatLocalTime = (date) => {
  if (!date) return "—";
  return date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
};

const formatDayLabel = (prog) => (prog.day_of_week === -1 ? "Every day" : DAY_LABELS[prog.day_of_week] + " only");

const formatCountdown = (ms) => {
  if (ms <= 0) return { d: 0, h: 0, m: 0, s: 0 };
  const total = Math.floor(ms / 1000);
  return {
    d: Math.floor(total / 86400),
    h: Math.floor((total % 86400) / 3600),
    m: Math.floor((total % 3600) / 60),
    s: total % 60,
  };
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export default function LiveStreamSchedule() {
  const [programmes, setProgrammes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(new Date());

  const visitorTz = useMemo(() => {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "your local time"; }
    catch { return "your local time"; }
  }, []);

  useEffect(() => {
    api.get("/tv/live-schedule").then(({ data }) => setProgrammes(data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  // Tick every second — drives the countdown timer
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  // Compute next occurrence for each programme + find the soonest one
  const withOccurrences = useMemo(() => {
    return programmes
      .map((p) => ({ ...p, next: nextOccurrence(p, now) }))
      .filter((p) => p.next)
      .sort((a, b) => a.next - b.next);
  }, [programmes, now]);

  const nextUp = withOccurrences[0];
  const countdown = nextUp ? formatCountdown(nextUp.next.getTime() - now.getTime()) : null;

  if (loading) {
    return (
      <section className="border-t border-gold-500/15 py-16">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 flex items-center justify-center text-white/50">
          <Loader2 size={20} className="animate-spin text-gold-400 mr-3" />
          <span className="uppercase tracking-[0.2em] text-xs">Loading live schedule…</span>
        </div>
      </section>
    );
  }
  if (programmes.length === 0) return null;

  return (
    <section
      id="live-stream-schedule"
      data-testid="live-stream-schedule"
      className="relative border-t border-gold-500/15 py-16 md:py-24 overflow-hidden"
    >
      {/* Kingdom Blue ambient glow */}
      <div className="pointer-events-none absolute inset-0 opacity-70">
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 h-[600px] w-[900px] rounded-full bg-ink-royal/60 blur-[140px]" />
        <div className="absolute bottom-0 right-0 h-[380px] w-[380px] rounded-full bg-gold-500/8 blur-[120px]" />
      </div>

      <div className="relative mx-auto max-w-[1400px] px-6 md:px-10">
        {/* Header */}
        <div className="mb-10 md:mb-14">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-gold-500 mb-4">
            <Radio size={13} /> Live Stream Schedule
          </div>
          <div className="flex flex-wrap items-end justify-between gap-6">
            <h2 className="font-serif text-3xl md:text-5xl leading-tight text-white">
              Weekly <span className="font-accent italic text-gold-gradient">broadcast</span> schedule
            </h2>
            <p className="text-[11px] uppercase tracking-[0.24em] text-white/50 flex items-center gap-2">
              <Globe2 size={12} className="text-gold-400" />
              Times shown in <span className="text-gold-300">WAT (UTC+1)</span>
              <span className="text-white/30 mx-1">·</span>
              auto-adjusted to <span className="text-white/80">{visitorTz}</span>
            </p>
          </div>
        </div>

        {/* Countdown card */}
        {nextUp && countdown && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="mb-10 md:mb-14 border border-gold-500/30 bg-gradient-to-r from-ink-royal/50 via-ink to-ink-royal/50 backdrop-blur px-6 py-6 md:px-10 md:py-8 grid gap-6 md:grid-cols-[1fr_auto] items-center"
            data-testid="live-schedule-countdown"
          >
            <div>
              <p className="text-[10px] uppercase tracking-[0.32em] text-gold-400 mb-2 flex items-center gap-2">
                <span className="relative inline-flex w-2 h-2">
                  <span className="absolute inline-flex w-full h-full rounded-full bg-red-500 opacity-75 animate-ping" />
                  <span className="relative inline-flex w-2 h-2 rounded-full bg-red-500" />
                </span>
                Next up on HBTV
              </p>
              <p className="font-serif text-2xl md:text-4xl text-white leading-tight" data-testid="live-schedule-next-name">
                {nextUp.programme_name}
              </p>
              <p className="mt-2 text-white/60 text-sm md:text-base">
                {formatWATTime(nextUp)} WAT · {formatLocalTime(nextUp.next)} your time · {nextUp.next.toLocaleDateString(undefined, { weekday: "long" })}
              </p>
            </div>
            <div className="flex items-center gap-3 md:gap-4" data-testid="live-schedule-countdown-timer">
              {[
                { v: countdown.d, label: "days" },
                { v: countdown.h, label: "hrs" },
                { v: countdown.m, label: "min" },
                { v: countdown.s, label: "sec" },
              ].map(({ v, label }) => (
                <div key={label} className="text-center min-w-[64px] md:min-w-[80px]">
                  <div className="border border-gold-500/40 bg-ink/70 backdrop-blur px-3 py-3 md:px-4 md:py-4 font-serif text-2xl md:text-4xl text-gold-gradient tabular-nums">
                    {String(v).padStart(2, "0")}
                  </div>
                  <p className="mt-2 text-[9px] md:text-[10px] uppercase tracking-[0.3em] text-white/45">{label}</p>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Programme table */}
        <div className="border border-gold-500/25 overflow-hidden" data-testid="live-schedule-table">
          {/* Table header */}
          <div className="hidden md:grid grid-cols-[1.5fr_1.5fr_1fr_1fr_1fr] gap-4 bg-ink-royal/60 border-b border-gold-500/40 px-6 py-4">
            {["Programme", "Description", "WAT", "Your time", "Days"].map((h) => (
              <p key={h} className="text-[10px] uppercase tracking-[0.28em] text-gold-400 font-semibold">{h}</p>
            ))}
          </div>
          {/* Rows */}
          <div className="divide-y divide-gold-500/15">
            {withOccurrences.map((p, i) => {
              const isNext = i === 0;
              return (
                <motion.div
                  key={p.id}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ delay: i * 0.04, duration: 0.5 }}
                  data-testid={`live-schedule-row-${p.id}`}
                  className={`grid md:grid-cols-[1.5fr_1.5fr_1fr_1fr_1fr] gap-3 md:gap-4 px-5 py-5 md:px-6 md:py-5 transition-colors duration-300 ${
                    isNext ? "bg-gold-500/5" : "hover:bg-ink-paper/40"
                  }`}
                >
                  {/* Programme name */}
                  <div className="flex items-center gap-3 min-w-0">
                    {isNext && (
                      <span className="hidden md:inline-flex shrink-0 items-center gap-1.5 border border-gold-400 text-gold-300 px-2 py-0.5 text-[9px] uppercase tracking-[0.22em]">
                        <span className="w-1.5 h-1.5 rounded-full bg-gold-400 animate-pulse" /> Next
                      </span>
                    )}
                    <p className="font-serif text-white text-lg md:text-xl leading-tight">{p.programme_name}</p>
                  </div>
                  {/* Description */}
                  <p className="text-white/60 text-sm leading-snug md:pr-4">{p.description}</p>
                  {/* WAT time */}
                  <div className="flex items-baseline gap-1.5 md:block">
                    <p className="md:hidden text-[10px] uppercase tracking-[0.22em] text-gold-500">WAT</p>
                    <p className="text-gold-300 font-serif text-lg md:text-xl tracking-tight">{formatWATTime(p)}</p>
                  </div>
                  {/* Local time */}
                  <div className="flex items-baseline gap-1.5 md:block">
                    <p className="md:hidden text-[10px] uppercase tracking-[0.22em] text-gold-500">Your time</p>
                    <p className="text-white font-serif text-lg md:text-xl tracking-tight" data-testid={`live-local-${p.id}`}>{formatLocalTime(p.next)}</p>
                  </div>
                  {/* Days */}
                  <div className="flex items-center gap-1.5 md:block">
                    <Calendar size={12} className="text-gold-500 md:hidden" />
                    <p className="text-[11px] uppercase tracking-[0.22em] text-white/70">{formatDayLabel(p)}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Footnote */}
        <p className="mt-6 text-[11px] uppercase tracking-[0.22em] text-white/40 flex items-center gap-2">
          <Clock size={12} className="text-gold-500" />
          Schedule refreshes automatically · Manage in Admin → HBTV
        </p>
      </div>
    </section>
  );
}
